import {
    Component,
    computed,
    effect,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { TemplateUser } from '@app/shared/models/ecmr/TemplateUser';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { TemplateOverviewService } from '@app/core/services/ecmr-services/template-overview.service';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { lastValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { Router } from '@angular/router';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { EcmrDetailDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-detail-dialog/ecmr-detail-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchStore } from '@app/stores/searchStore';
import { TablePreferencesStore } from '@app/stores/tablePreferencesStore';
import {
    ECMR_TEMPLATE_COLUMNS,
    ecmrColumnPreferences,
} from '@app/shared/components/ecmr/ecmr-table-columns';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/shared/material/material.module';
import { TranslateModule } from '@ngx-translate/core';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-templates-ecmr',
    templateUrl: './templates-ecmr.component.html',
    styleUrl: './templates-ecmr.component.scss',
    imports: [CommonModule, MaterialModule, TranslateModule],
})
export class TemplatesEcmrComponent implements OnInit, OnDestroy {
    baseRoute = input<string>();
    isReadOnly = input<boolean>(false);
    searchControl = input<FormControl>();

    private columnPreferences = ecmrColumnPreferences('templates');

    // Column order (drag & drop). Which columns are visible is chosen via
    // Show/Hide Columns in the paginated-table-filter.
    columnOrder = signal<string[]>([
        'id',
        ...ECMR_TEMPLATE_COLUMNS.map(column => String(column.key)),
    ]);
    private visibleColumns = computed(() =>
        this.tablePreferencesStore.getVisibleColumns(
            this.sessionService.getSession()._key,
            this.columnPreferences.viewKey,
            this.columnPreferences.columns.map(column => String(column.key)),
            this.columnPreferences.defaultHidden
        )
    );
    displayedColumns = computed(() =>
        this.columnOrder().filter(
            column => column === 'id' || this.visibleColumns().includes(column)
        )
    );

    selectedTemplate: TemplateUser | null = null;

    dataSource: MatTableDataSource<TemplateUser> =
        new MatTableDataSource<TemplateUser>();

    intervalId: ReturnType<typeof setInterval>;

    paginator = viewChild.required(MatPaginator);
    sort = viewChild(MatSort);

    private searchStore = inject(SearchStore);
    private tablePreferencesStore = inject(TablePreferencesStore);

    constructor(
        private overviewService: TemplateOverviewService,
        private _liveAnnouncer: LiveAnnouncer,
        private toastr: ToastrService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private router: Router,
        private dialog: MatDialog
    ) {
        // Listen to search state changes from the store
        effect(() => {
            const query = this.searchStore.searchText();
            this.dataSource.filter = query.trim().toLowerCase();
        });

        // Reload templates whenever the current organization site changes
        // (replaces the old currentOrganizationSite$ subscription, whose
        // re-firing on every site switch triggered an immediate refresh).
        effect(() => {
            // synchronous signal read so this effect tracks site changes
            this.sessionService.currentOrganizationSiteKey();
            this.startRefreshInterval();
        });
    }

    ngOnInit() {
        const savedColumns = this.overviewService.getDisplayedColumns();
        if (savedColumns) {
            this.columnOrder.set(savedColumns);
        }
    }

    ngOnDestroy() {
        clearInterval(this.intervalId);
    }

    async loadTemplates(siteKey: string) {
        try {
            const templates: TemplateUser[] = await lastValueFrom(
                this.overviewService.getAllTemplates(siteKey)
            );

            this.dataSource.data = templates;

            this.dataSource.filterPredicate = (data, filter) => {
                let dataStr = Object.values(data).join(' ').toLowerCase();
                dataStr +=
                    data.ecmr.ecmrConsignment?.referenceIdentificationNumber?.value?.toLowerCase() ||
                    '';
                dataStr +=
                    data.ecmr.ecmrConsignment?.senderInformation.senderCompanyName?.toLowerCase() ||
                    '';
                dataStr +=
                    data.ecmr.ecmrConsignment?.consigneeInformation.consigneeCompanyName?.toLowerCase() ||
                    '';
                return dataStr.includes(filter);
            };

            this.dataSource.paginator = this.paginator();
        } catch (e) {
            this.toastr.error(
                this.translateService.instant(
                    'ECMR.template_overview.loading_error'
                )
            );
        }
    }

    async startRefreshInterval() {
        await this.loadTemplates(
            this.sessionService.currentOrganizationSiteKey()
        );
        clearInterval(this.intervalId);
        this.intervalId = setInterval(() => {
            this.loadTemplates(
                this.sessionService.currentOrganizationSiteKey()
            );
        }, 60 * 1000);
    }

    editTemplate(templateUser: TemplateUser) {
        this.router.navigate([
            this.baseRoute() +
                '/' +
                RoutingEndpoints.ecmr_template_editor +
                `/${templateUser.id}`,
        ]);
    }

    async deleteTemplate(templateUser: TemplateUser) {
        try {
            await lastValueFrom(
                this.overviewService.deleteTemplate(
                    this.sessionService.currentOrganizationSiteKey(),
                    templateUser.id
                )
            );

            this.toastr.success(
                this.translateService.instant(
                    'ECMR.template_overview.delete_success'
                )
            );
            await this.loadTemplates(
                this.sessionService.currentOrganizationSiteKey()
            );
        } catch (e) {
            this.toastr.error(
                this.translateService.instant(
                    'ECMR.template_overview.delete_error'
                )
            );
        }
    }

    sortData(sort: Sort) {
        this.announceSortChange(sort);

        const data = this.dataSource.data.slice();
        this.dataSource.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'number':
                    return compare(
                        a.templateUserNumber,
                        b.templateUserNumber,
                        isAsc
                    );
                case 'name':
                    return compare(a.name, b.name, isAsc);
                case 'refId':
                    return compare(
                        a.ecmr.ecmrConsignment.referenceIdentificationNumber
                            .value,
                        b.ecmr.ecmrConsignment.referenceIdentificationNumber
                            .value,
                        isAsc
                    );
                case 'from':
                    return compare(
                        a.ecmr.ecmrConsignment.senderInformation
                            .senderCompanyName,
                        b.ecmr.ecmrConsignment.senderInformation
                            .senderCompanyName,
                        isAsc
                    );
                case 'to':
                    return compare(
                        a.ecmr.ecmrConsignment.consigneeInformation
                            .consigneeCompanyName,
                        b.ecmr.ecmrConsignment.consigneeInformation
                            .consigneeCompanyName,
                        isAsc
                    );
                default:
                    return 0;
            }
        });
    }

    tableDrop(event: CdkDragDrop<string[]>) {
        // the id header is not draggable, so drag indices start one column later
        const displayed = this.displayedColumns();
        const order = [...this.columnOrder()];
        moveItemInArray(
            order,
            order.indexOf(displayed[event.previousIndex + 1]),
            order.indexOf(displayed[event.currentIndex + 1])
        );
        this.columnOrder.set(order);
    }

    announceSortChange(sortState: Sort) {
        if (sortState.direction) {
            this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
        } else {
            this._liveAnnouncer.announce('Sorting cleared');
        }
    }

    showDetailsForEcmrAtIndex(index: number) {
        const dialogRef = this.dialog.open(EcmrDetailDialogComponent, {
            width: '80vw',
            height: '80vh',
            disableClose: false,
            data: {
                baseRoute: this.baseRoute(),
                ecmr: this.dataSource.data[index].ecmr,
                template: this.dataSource.data[index],
                isArchive: false,
                isReadOnly: this.isReadOnly(),
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
            }
        });
    }
}

function compare(
    a: number | string | Date | null,
    b: number | string | Date | null,
    isAsc: boolean
) {
    if (a && b) {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
    return 0;
}
